/**
 * Entity Forms Panel.
 *
 * Reusable panel that shows form submissions attached to an entity and
 * allows filling out new forms. Supports both generic forms and inspection
 * checklists with pass/fail result badges and inspector notes.
 *
 * Used on AssetDetailPage and WorkOrderDetailPage "Forms" tabs.
 * For work orders, can show attached inspection forms prominently.
 */

import { useState } from "react";
import {
  FileText,
  Plus,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ClipboardCheck,
} from "lucide-react";
import {
  useFormTemplates,
  useFormSubmissions,
  useSubmitForm,
} from "@/features/forms/api";
import type { FormTemplate } from "@/types/form";
import FormRenderer from "./FormRenderer";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";

interface EntityFormsPanelProps {
  entityType: string;
  entityId: string;
  workOrderId?: string;
  assetId?: string;
  attachedTemplateIds?: string[];
}

const resultBadge = (result: string | null) => {
  if (result === "pass")
    return (
      <Badge variant="success" dot>
        <CheckCircle2 className="h-3 w-3" /> Pass
      </Badge>
    );
  if (result === "attention")
    return (
      <Badge variant="warning" dot>
        <AlertTriangle className="h-3 w-3" /> Attention
      </Badge>
    );
  if (result === "fail")
    return (
      <Badge variant="danger" dot>
        <XCircle className="h-3 w-3" /> Fail
      </Badge>
    );
  return null;
};

function EntityFormsPanel({
  entityType,
  entityId,
  workOrderId,
  assetId,
  attachedTemplateIds,
}: EntityFormsPanelProps) {
  const { data: templates } = useFormTemplates(entityType);
  const { data: submissions, isLoading } = useFormSubmissions(
    entityType,
    entityId,
  );

  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(
    null,
  );
  const [inspectorNotes, setInspectorNotes] = useState("");
  const submitMutation = useSubmitForm(selectedTemplate?.id || "");

  const handleFormSubmit = async (data: Record<string, unknown>) => {
    if (!selectedTemplate) return;
    await submitMutation.mutateAsync({
      entity_type: entityType,
      entity_id: entityId,
      data,
      work_order_id: workOrderId,
      asset_id: assetId,
      inspector_notes: inspectorNotes || undefined,
    });
    setSelectedTemplate(null);
    setInspectorNotes("");
  };

  // Separate attached templates (from WO) from available templates.
  const attachedTemplates = (templates || []).filter((t) =>
    attachedTemplateIds?.includes(t.id),
  );
  const otherTemplates = (templates || []).filter(
    (t) => !attachedTemplateIds?.includes(t.id),
  );

  // Check which attached forms are already completed.
  const completedTemplateIds = new Set(
    (submissions || []).map((s) => s.form_template_id),
  );

  return (
    <div className="space-y-4">
      {/* Attached inspection forms (from work order) — shown prominently */}
      {attachedTemplates.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
            Required Forms
          </h4>
          {attachedTemplates.map((t) => {
            const isCompleted = completedTemplateIds.has(t.id);
            const submission = (submissions || []).find(
              (s) => s.form_template_id === t.id,
            );
            return (
              <Card
                key={t.id}
                padding="sm"
                className={
                  isCompleted
                    ? "border-[var(--text-success)]/20"
                    : "border-[var(--text-warning)]/20"
                }
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ClipboardCheck
                      className={`h-4 w-4 ${isCompleted ? "text-[var(--text-success)]" : "text-[var(--text-warning)]"}`}
                    />
                    <span className="text-sm font-semibold text-[var(--text-primary)]">
                      {t.name}
                    </span>
                    {t.template_type === "inspection" && (
                      <Badge variant="info">Inspection</Badge>
                    )}
                    {isCompleted ? (
                      <>
                        <Badge variant="success" dot>
                          Completed
                        </Badge>
                        {submission?.overall_result &&
                          resultBadge(submission.overall_result)}
                      </>
                    ) : (
                      <Badge variant="warning" dot>
                        Pending
                      </Badge>
                    )}
                  </div>
                  {!isCompleted && !selectedTemplate && (
                    <Button size="sm" onClick={() => setSelectedTemplate(t)}>
                      Fill Out
                    </Button>
                  )}
                </div>
                {t.description && (
                  <p className="text-xs text-[var(--text-tertiary)] mt-1">
                    {t.description}
                  </p>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Other available forms */}
      {!selectedTemplate && otherTemplates.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)] mb-2">
            Additional Forms
          </h4>
          <div className="flex flex-wrap gap-2">
            {otherTemplates.map((t) => (
              <Button
                key={t.id}
                size="sm"
                variant="secondary"
                onClick={() => setSelectedTemplate(t)}
              >
                <Plus className="h-3.5 w-3.5" /> {t.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Active form filling */}
      {selectedTemplate && (
        <Card className="border-brand-200 dark:border-brand-800">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardCheck className="h-5 w-5 text-[var(--color-interactive-primary)]" />
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">
              {selectedTemplate.name}
            </h3>
            {selectedTemplate.template_type === "inspection" && (
              <Badge variant="info">Inspection</Badge>
            )}
          </div>
          {selectedTemplate.description && (
            <p className="text-xs text-[var(--text-secondary)] mb-4">
              {selectedTemplate.description}
            </p>
          )}
          <FormRenderer
            fields={selectedTemplate.schema}
            onSubmit={handleFormSubmit}
            isSubmitting={submitMutation.isPending}
            onCancel={() => {
              setSelectedTemplate(null);
              setInspectorNotes("");
            }}
            showInspectorNotes={selectedTemplate.template_type === "inspection"}
            onInspectorNotesChange={setInspectorNotes}
          />
        </Card>
      )}

      {/* Submissions history */}
      {isLoading ? (
        <p className="text-sm text-[var(--text-tertiary)]">
          Loading submissions...
        </p>
      ) : submissions && submissions.length > 0 ? (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
            Submission History
          </h4>
          {submissions.map((sub) => (
            <Card key={sub.id} padding="sm">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <FileText className="h-4 w-4 text-[var(--text-tertiary)]" />
                  <span className="text-xs text-[var(--text-secondary)]">
                    {new Date(sub.submitted_at).toLocaleString()}
                  </span>
                  {sub.overall_result && resultBadge(sub.overall_result)}
                </div>
              </div>
              {sub.inspector_notes && (
                <p className="text-xs text-[var(--text-secondary)] mt-1.5 italic">
                  {sub.inspector_notes}
                </p>
              )}
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {Object.entries(sub.data).map(([key, value]) => {
                  const strVal = String(value);
                  const isRating =
                    strVal === "Pass" ||
                    strVal === "Attention" ||
                    strVal === "Fail";
                  return (
                    <div key={key}>
                      <p className="text-[10px] text-[var(--text-tertiary)]">
                        {key.replace(/_/g, " ")}
                      </p>
                      <p
                        className={`text-sm font-medium ${
                          isRating && strVal === "Pass"
                            ? "text-[var(--text-success)]"
                            : isRating && strVal === "Attention"
                              ? "text-[var(--text-warning)]"
                              : isRating && strVal === "Fail"
                                ? "text-[var(--text-danger)]"
                                : "text-[var(--text-primary)]"
                        }`}
                      >
                        {strVal}
                      </p>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      ) : !selectedTemplate ? (
        <p className="text-sm text-[var(--text-tertiary)]">
          No form submissions yet.
        </p>
      ) : null}
    </div>
  );
}

export default EntityFormsPanel;
